export type TourTier = 'all' | 'pro' | 'chef_master';
export type TourRole = 'all' | 'helper' | 'admin' | 'owner' | 'moderator';

export interface TourStep {
  id: string;
  titleKey: string;
  descriptionKey: string;
  icon: string;
  route?: string;
  targetSelector?: string;
  tier: TourTier;
  upgradeTeaser: boolean;
  role?: TourRole;
}

export interface Tour {
  name: string;
  labelKey: string;
  steps: TourStep[];
  role?: TourRole;
}

export const ALL_TOURS: Tour[] = [
  // ─── 1. WELCOME ───────────────────────────────────────────────────
  {
    name: 'welcome',
    labelKey: 'tour.welcome.label',
    steps: [
      {
        id: 'welcome-intro',
        titleKey: 'tour.welcome.intro.title',
        descriptionKey: 'tour.welcome.intro.description',
        icon: 'ChefHat',
        route: '/',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'welcome-ai',
        titleKey: 'tour.welcome.ai.title',
        descriptionKey: 'tour.welcome.ai.description',
        icon: 'Sparkles',
        route: '/chaos',
        targetSelector: '[data-tour="chaos-kitchen-generate"]',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'welcome-recipes',
        titleKey: 'tour.welcome.recipes.title',
        descriptionKey: 'tour.welcome.recipes.description',
        icon: 'BookOpen',
        route: '/recipes',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'welcome-meal-plan',
        titleKey: 'tour.welcome.mealPlan.title',
        descriptionKey: 'tour.welcome.mealPlan.description',
        icon: 'Calendar',
        route: '/meal-planner',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'welcome-livestream',
        titleKey: 'tour.welcome.livestream.title',
        descriptionKey: 'tour.welcome.livestream.description',
        icon: 'Radio',
        route: '/live',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'welcome-gamification',
        titleKey: 'tour.welcome.gamification.title',
        descriptionKey: 'tour.welcome.gamification.description',
        icon: 'Trophy',
        route: '/',
        targetSelector: '[data-tour="xp-badge"]',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'welcome-community',
        titleKey: 'tour.welcome.community.title',
        descriptionKey: 'tour.welcome.community.description',
        icon: 'Users',
        route: '/community',
        targetSelector: '[data-tour="community-feed"]',
        tier: 'all',
        upgradeTeaser: false,
      },
    ],
  },

  // ─── 2. AI FEATURES ───────────────────────────────────────────────
  {
    name: 'ai-features',
    labelKey: 'tour.aiFeatures.label',
    steps: [
      {
        id: 'ai-chaos-kitchen',
        titleKey: 'tour.aiFeatures.chaosKitchen.title',
        descriptionKey: 'tour.aiFeatures.chaosKitchen.description',
        icon: 'Flame',
        route: '/chaos',
        targetSelector: '[data-tour="chaos-kitchen-ingredients"]',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'ai-cursed-mode',
        titleKey: 'tour.aiFeatures.cursedMode.title',
        descriptionKey: 'tour.aiFeatures.cursedMode.description',
        icon: 'Zap',
        route: '/cursed',
        targetSelector: '[data-tour="cursed-mode-generate"]',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'ai-transformer',
        titleKey: 'tour.aiFeatures.transformer.title',
        descriptionKey: 'tour.aiFeatures.transformer.description',
        icon: 'Wand2',
        route: '/transform',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'ai-meal-planner',
        titleKey: 'tour.aiFeatures.mealPlanner.title',
        descriptionKey: 'tour.aiFeatures.mealPlanner.description',
        icon: 'Calendar',
        route: '/meal-planner',
        targetSelector: '[data-tour="meal-planner-ai"]',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'ai-bulk',
        titleKey: 'tour.aiFeatures.bulkAI.title',
        descriptionKey: 'tour.aiFeatures.bulkAI.description',
        icon: 'Brain',
        route: '/bulk-actions',
        tier: 'pro',
        upgradeTeaser: true,
      },
      {
        id: 'ai-photo',
        titleKey: 'tour.aiFeatures.photoAnalysis.title',
        descriptionKey: 'tour.aiFeatures.photoAnalysis.description',
        icon: 'Camera',
        route: '/chaos',
        tier: 'all',
        upgradeTeaser: false,
      },
    ],
  },

  // ─── 3. RECIPE SYSTEM ─────────────────────────────────────────────
  {
    name: 'recipe-system',
    labelKey: 'tour.recipeSystem.label',
    steps: [
      {
        id: 'recipe-create',
        titleKey: 'tour.recipeSystem.create.title',
        descriptionKey: 'tour.recipeSystem.create.description',
        icon: 'Plus',
        route: '/recipes',
        targetSelector: '[data-tour="recipe-create"]',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'recipe-import',
        titleKey: 'tour.recipeSystem.import.title',
        descriptionKey: 'tour.recipeSystem.import.description',
        icon: 'Link',
        route: '/recipes',
        targetSelector: '[data-tour="recipe-import"]',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'recipe-nutrition',
        titleKey: 'tour.recipeSystem.nutrition.title',
        descriptionKey: 'tour.recipeSystem.nutrition.description',
        icon: 'Calculator',
        route: '/recipes',
        targetSelector: '[data-tour="nutrition-calc"]',
        tier: 'chef_master',
        upgradeTeaser: true,
      },
      {
        id: 'recipe-collections',
        titleKey: 'tour.recipeSystem.collections.title',
        descriptionKey: 'tour.recipeSystem.collections.description',
        icon: 'BookOpen',
        route: '/collections',
        targetSelector: '[data-tour="collection-save"]',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'recipe-offline',
        titleKey: 'tour.recipeSystem.offline.title',
        descriptionKey: 'tour.recipeSystem.offline.description',
        icon: 'Download',
        route: '/offline-recipes',
        tier: 'all',
        upgradeTeaser: false,
      },
    ],
  },

  // ─── 4. MEAL PLANNER ──────────────────────────────────────────────
  {
    name: 'meal-planner',
    labelKey: 'tour.mealPlanner.label',
    steps: [
      {
        id: 'planner-intro',
        titleKey: 'tour.mealPlanner.intro.title',
        descriptionKey: 'tour.mealPlanner.intro.description',
        icon: 'Calendar',
        route: '/meal-planner',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'planner-ai',
        titleKey: 'tour.mealPlanner.aiPlan.title',
        descriptionKey: 'tour.mealPlanner.aiPlan.description',
        icon: 'Sparkles',
        route: '/meal-planner',
        targetSelector: '[data-tour="meal-planner-ai"]',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'planner-shopping',
        titleKey: 'tour.mealPlanner.shopping.title',
        descriptionKey: 'tour.mealPlanner.shopping.description',
        icon: 'ShoppingCart',
        route: '/shopping-list',
        tier: 'all',
        upgradeTeaser: false,
      },
    ],
  },

  // ─── 5. COMMUNITY ─────────────────────────────────────────────────
  {
    name: 'community',
    labelKey: 'tour.community.label',
    steps: [
      {
        id: 'community-feed',
        titleKey: 'tour.community.feed.title',
        descriptionKey: 'tour.community.feed.description',
        icon: 'Users',
        route: '/community',
        targetSelector: '[data-tour="community-feed"]',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'community-challenges',
        titleKey: 'tour.community.challenges.title',
        descriptionKey: 'tour.community.challenges.description',
        icon: 'Trophy',
        route: '/challenges',
        targetSelector: '[data-tour="challenges"]',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'community-leaderboard',
        titleKey: 'tour.community.leaderboard.title',
        descriptionKey: 'tour.community.leaderboard.description',
        icon: 'Award',
        route: '/leaderboard',
        targetSelector: '[data-tour="leaderboard"]',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'community-profiles',
        titleKey: 'tour.community.profiles.title',
        descriptionKey: 'tour.community.profiles.description',
        icon: 'User',
        route: '/community',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'community-discord',
        titleKey: 'tour.community.discord.title',
        descriptionKey: 'tour.community.discord.description',
        icon: 'Users',
        route: '/community',
        tier: 'all',
        upgradeTeaser: false,
      },
    ],
  },

  // ─── 6. GAMIFICATION ──────────────────────────────────────────────
  {
    name: 'gamification',
    labelKey: 'tour.gamification.label',
    steps: [
      {
        id: 'gamification-xp',
        titleKey: 'tour.gamification.xp.title',
        descriptionKey: 'tour.gamification.xp.description',
        icon: 'Zap',
        route: '/',
        targetSelector: '[data-tour="xp-badge"]',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'gamification-streak',
        titleKey: 'tour.gamification.streak.title',
        descriptionKey: 'tour.gamification.streak.description',
        icon: 'Flame',
        route: '/',
        targetSelector: '[data-tour="streak-display"]',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'gamification-checkin',
        titleKey: 'tour.gamification.checkin.title',
        descriptionKey: 'tour.gamification.checkin.description',
        icon: 'CheckCircle',
        route: '/',
        targetSelector: '[data-tour="daily-checkin"]',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'gamification-badges',
        titleKey: 'tour.gamification.badges.title',
        descriptionKey: 'tour.gamification.badges.description',
        icon: 'Award',
        route: '/profile',
        targetSelector: '[data-tour="xp-badge"]',
        tier: 'all',
        upgradeTeaser: false,
      },
    ],
  },

  // ─── 7. LIVESTREAM ────────────────────────────────────────────────
  {
    name: 'livestream',
    labelKey: 'tour.livestream.label',
    steps: [
      {
        id: 'livestream-discover',
        titleKey: 'tour.livestream.discover.title',
        descriptionKey: 'tour.livestream.discover.description',
        icon: 'Radio',
        route: '/live',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'livestream-golive',
        titleKey: 'tour.livestream.goLive.title',
        descriptionKey: 'tour.livestream.goLive.description',
        icon: 'Video',
        route: '/live',
        targetSelector: '[data-tour="go-live"]',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'livestream-overlays',
        titleKey: 'tour.livestream.overlays.title',
        descriptionKey: 'tour.livestream.overlays.description',
        icon: 'Monitor',
        route: '/live',
        tier: 'pro',
        upgradeTeaser: true,
      },
      {
        id: 'livestream-obs',
        titleKey: 'tour.livestream.obs.title',
        descriptionKey: 'tour.livestream.obs.description',
        icon: 'Monitor',
        route: '/live/obs-plugin',
        tier: 'pro',
        upgradeTeaser: true,
      },
      {
        id: 'livestream-alerts',
        titleKey: 'tour.livestream.alerts.title',
        descriptionKey: 'tour.livestream.alerts.description',
        icon: 'Bell',
        route: '/live',
        tier: 'pro',
        upgradeTeaser: true,
      },
      {
        id: 'livestream-analytics',
        titleKey: 'tour.livestream.analytics.title',
        descriptionKey: 'tour.livestream.analytics.description',
        icon: 'BarChart3',
        route: '/live',
        tier: 'pro',
        upgradeTeaser: true,
      },
    ],
  },

  // ─── 8. CREATOR TOOLS ─────────────────────────────────────────────
  {
    name: 'creator-tools',
    labelKey: 'tour.creatorTools.label',
    steps: [
      {
        id: 'creator-dashboard',
        titleKey: 'tour.creatorTools.dashboard.title',
        descriptionKey: 'tour.creatorTools.dashboard.description',
        icon: 'LayoutDashboard',
        route: '/creator/dashboard',
        targetSelector: '[data-tour="creator-dashboard"]',
        tier: 'pro',
        upgradeTeaser: true,
      },
      {
        id: 'creator-monetization',
        titleKey: 'tour.creatorTools.monetization.title',
        descriptionKey: 'tour.creatorTools.monetization.description',
        icon: 'DollarSign',
        route: '/creator/dashboard',
        tier: 'pro',
        upgradeTeaser: true,
      },
      {
        id: 'creator-supporters',
        titleKey: 'tour.creatorTools.supporters.title',
        descriptionKey: 'tour.creatorTools.supporters.description',
        icon: 'Heart',
        route: '/creator/dashboard',
        tier: 'pro',
        upgradeTeaser: true,
      },
      {
        id: 'creator-tokens',
        titleKey: 'tour.creatorTools.tokens.title',
        descriptionKey: 'tour.creatorTools.tokens.description',
        icon: 'Coins',
        route: '/tokens/buy',
        tier: 'all',
        upgradeTeaser: false,
      },
    ],
  },

  // ─── 9. PREMIUM FEATURES ──────────────────────────────────────────
  {
    name: 'premium-features',
    labelKey: 'tour.premiumFeatures.label',
    steps: [
      {
        id: 'premium-credits',
        titleKey: 'tour.premiumFeatures.credits.title',
        descriptionKey: 'tour.premiumFeatures.credits.description',
        icon: 'Coins',
        route: '/subscription',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'premium-pro',
        titleKey: 'tour.premiumFeatures.pro.title',
        descriptionKey: 'tour.premiumFeatures.pro.description',
        icon: 'Sparkles',
        route: '/pricing',
        tier: 'pro',
        upgradeTeaser: true,
      },
      {
        id: 'premium-chefmaster',
        titleKey: 'tour.premiumFeatures.chefMaster.title',
        descriptionKey: 'tour.premiumFeatures.chefMaster.description',
        icon: 'Crown',
        route: '/pricing',
        tier: 'chef_master',
        upgradeTeaser: true,
      },
      {
        id: 'premium-family',
        titleKey: 'tour.premiumFeatures.family.title',
        descriptionKey: 'tour.premiumFeatures.family.description',
        icon: 'Users',
        route: '/pricing',
        tier: 'chef_master',
        upgradeTeaser: true,
      },
    ],
  },

  // ─── 10. FAMILY SHARING ───────────────────────────────────────────
  {
    name: 'family-sharing',
    labelKey: 'tour.familySharing.label',
    steps: [
      {
        id: 'family-intro',
        titleKey: 'tour.familySharing.intro.title',
        descriptionKey: 'tour.familySharing.intro.description',
        icon: 'Users',
        route: '/profile',
        targetSelector: '[data-tour="family-sharing"]',
        tier: 'chef_master',
        upgradeTeaser: false,
      },
      {
        id: 'family-invite',
        titleKey: 'tour.familySharing.invite.title',
        descriptionKey: 'tour.familySharing.invite.description',
        icon: 'UserPlus',
        route: '/profile',
        tier: 'chef_master',
        upgradeTeaser: false,
      },
      {
        id: 'family-benefits',
        titleKey: 'tour.familySharing.benefits.title',
        descriptionKey: 'tour.familySharing.benefits.description',
        icon: 'Gift',
        route: '/profile',
        tier: 'chef_master',
        upgradeTeaser: false,
      },
    ],
  },

  // ─── 11. SEASONAL EVENTS ──────────────────────────────────────────
  {
    name: 'seasonal-events',
    labelKey: 'tour.seasonalEvents.label',
    steps: [
      {
        id: 'seasonal-advent',
        titleKey: 'tour.seasonalEvents.advent.title',
        descriptionKey: 'tour.seasonalEvents.advent.description',
        icon: 'Snowflake',
        route: '/advent',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'seasonal-challenges',
        titleKey: 'tour.seasonalEvents.challenges.title',
        descriptionKey: 'tour.seasonalEvents.challenges.description',
        icon: 'Star',
        route: '/challenges',
        tier: 'all',
        upgradeTeaser: false,
      },
    ],
  },

  // ─── 12. KITCHEN SETUP ────────────────────────────────────────────
  {
    name: 'kitchen-setup',
    labelKey: 'tour.kitchenSetup.label',
    steps: [
      {
        id: 'setup-profile',
        titleKey: 'tour.kitchenSetup.profile.title',
        descriptionKey: 'tour.kitchenSetup.profile.description',
        icon: 'User',
        route: '/profile',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'setup-notifications',
        titleKey: 'tour.kitchenSetup.notifications.title',
        descriptionKey: 'tour.kitchenSetup.notifications.description',
        icon: 'Bell',
        route: '/settings/notifications',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'setup-download',
        titleKey: 'tour.kitchenSetup.download.title',
        descriptionKey: 'tour.kitchenSetup.download.description',
        icon: 'Download',
        route: '/download',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'setup-offline',
        titleKey: 'tour.kitchenSetup.offline.title',
        descriptionKey: 'tour.kitchenSetup.offline.description',
        icon: 'Snowflake',
        route: '/offline-recipes',
        tier: 'all',
        upgradeTeaser: false,
      },
    ],
  },

  // ─── 13. CREDITS & TOKENS ────────────────────────────────────────
  {
    name: 'credits-tokens',
    labelKey: 'tour.economy.label',
    steps: [
      {
        id: 'economy-credits',
        titleKey: 'tour.economy.credits.title',
        descriptionKey: 'tour.economy.credits.description',
        icon: 'Coins',
        route: '/credits',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'economy-ai-analytics',
        titleKey: 'tour.economy.aiAnalytics.title',
        descriptionKey: 'tour.economy.aiAnalytics.description',
        icon: 'BarChart3',
        route: '/ai-analytics',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'economy-tokens',
        titleKey: 'tour.economy.tokens.title',
        descriptionKey: 'tour.economy.tokens.description',
        icon: 'Coins',
        route: '/tokens/buy',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'economy-streak-recovery',
        titleKey: 'tour.economy.streakRecovery.title',
        descriptionKey: 'tour.economy.streakRecovery.description',
        icon: 'Flame',
        route: '/streak-history',
        tier: 'pro',
        upgradeTeaser: true,
      },
    ],
  },

  // ─── 14. RECIPE BOOK ─────────────────────────────────────────────
  {
    name: 'recipe-book',
    labelKey: 'tour.recipeBook.label',
    steps: [
      {
        id: 'book-intro',
        titleKey: 'tour.recipeBook.intro.title',
        descriptionKey: 'tour.recipeBook.intro.description',
        icon: 'BookOpen',
        route: '/recipe-book',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'book-ai-pick',
        titleKey: 'tour.recipeBook.aiPick.title',
        descriptionKey: 'tour.recipeBook.aiPick.description',
        icon: 'Brain',
        route: '/recipe-book',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'book-shipping',
        titleKey: 'tour.recipeBook.shipping.title',
        descriptionKey: 'tour.recipeBook.shipping.description',
        icon: 'Gift',
        route: '/recipe-book',
        tier: 'all',
        upgradeTeaser: false,
      },
    ],
  },

  // ─── 15. FEEDBACK & ROADMAP ───────────────────────────────────────
  {
    name: 'feedback-roadmap',
    labelKey: 'tour.voice.label',
    steps: [
      {
        id: 'voice-feedback',
        titleKey: 'tour.voice.feedback.title',
        descriptionKey: 'tour.voice.feedback.description',
        icon: 'Heart',
        route: '/feedback',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'voice-roadmap',
        titleKey: 'tour.voice.roadmap.title',
        descriptionKey: 'tour.voice.roadmap.description',
        icon: 'Star',
        route: '/roadmap',
        tier: 'all',
        upgradeTeaser: false,
      },
      {
        id: 'voice-changelog',
        titleKey: 'tour.voice.changelog.title',
        descriptionKey: 'tour.voice.changelog.description',
        icon: 'BookOpen',
        route: '/changelog',
        tier: 'all',
        upgradeTeaser: false,
      },
    ],
  },

  // ─── 16. HELPER ONBOARDING ──────────────────────────────────────
  {
    name: 'helper-onboarding',
    labelKey: 'tour.helper.label',
    role: 'helper',
    steps: [
      {
        id: 'helper-welcome',
        titleKey: 'tour.helper.welcome.title',
        descriptionKey: 'tour.helper.welcome.description',
        icon: 'Heart',
        route: '/',
        tier: 'all',
        upgradeTeaser: false,
        role: 'helper',
      },
      {
        id: 'helper-support-panel',
        titleKey: 'tour.helper.supportPanel.title',
        descriptionKey: 'tour.helper.supportPanel.description',
        icon: 'Users',
        route: '/helper-support',
        tier: 'all',
        upgradeTeaser: false,
        role: 'helper',
      },
      {
        id: 'helper-benefits',
        titleKey: 'tour.helper.benefits.title',
        descriptionKey: 'tour.helper.benefits.description',
        icon: 'Gift',
        route: '/',
        tier: 'all',
        upgradeTeaser: false,
        role: 'helper',
      },
      {
        id: 'helper-probation',
        titleKey: 'tour.helper.probation.title',
        descriptionKey: 'tour.helper.probation.description',
        icon: 'CheckCircle',
        route: '/',
        tier: 'all',
        upgradeTeaser: false,
        role: 'helper',
      },
      {
        id: 'helper-community',
        titleKey: 'tour.helper.community.title',
        descriptionKey: 'tour.helper.community.description',
        icon: 'Users',
        route: '/community',
        tier: 'all',
        upgradeTeaser: false,
        role: 'helper',
      },
    ],
  },

  // ─── 17. ADMIN / OWNER ONBOARDING ────────────────────────────────
  {
    name: 'admin-onboarding',
    labelKey: 'tour.admin.label',
    role: 'admin',
    steps: [
      {
        id: 'admin-welcome',
        titleKey: 'tour.admin.welcome.title',
        descriptionKey: 'tour.admin.welcome.description',
        icon: 'Crown',
        route: '/',
        tier: 'all',
        upgradeTeaser: false,
        role: 'admin',
      },
      {
        id: 'admin-trust-safety',
        titleKey: 'tour.admin.trustSafety.title',
        descriptionKey: 'tour.admin.trustSafety.description',
        icon: 'CheckCircle',
        route: '/admin',
        tier: 'all',
        upgradeTeaser: false,
        role: 'admin',
      },
      {
        id: 'admin-staff-tools',
        titleKey: 'tour.admin.staffTools.title',
        descriptionKey: 'tour.admin.staffTools.description',
        icon: 'Wand2',
        route: '/admin',
        tier: 'all',
        upgradeTeaser: false,
        role: 'admin',
      },
      {
        id: 'admin-analytics',
        titleKey: 'tour.admin.analytics.title',
        descriptionKey: 'tour.admin.analytics.description',
        icon: 'BarChart3',
        route: '/admin',
        tier: 'all',
        upgradeTeaser: false,
        role: 'admin',
      },
    ],
  },

  // ─── 18. MODERATOR ONBOARDING ────────────────────────────────────
  {
    name: 'moderator-onboarding',
    labelKey: 'tour.moderator.label',
    role: 'moderator',
    steps: [
      {
        id: 'moderator-welcome',
        titleKey: 'tour.moderator.welcome.title',
        descriptionKey: 'tour.moderator.welcome.description',
        icon: 'Users',
        route: '/',
        tier: 'all',
        upgradeTeaser: false,
        role: 'moderator',
      },
      {
        id: 'moderator-content',
        titleKey: 'tour.moderator.content.title',
        descriptionKey: 'tour.moderator.content.description',
        icon: 'CheckCircle',
        route: '/community',
        tier: 'all',
        upgradeTeaser: false,
        role: 'moderator',
      },
      {
        id: 'moderator-livestream',
        titleKey: 'tour.moderator.livestream.title',
        descriptionKey: 'tour.moderator.livestream.description',
        icon: 'Radio',
        route: '/live',
        tier: 'all',
        upgradeTeaser: false,
        role: 'moderator',
      },
    ],
  },
];

export const getTour = (name: string): Tour | undefined =>
  ALL_TOURS.find(t => t.name === name);

export const TOUR_NAMES = ALL_TOURS.map(t => t.name);
